/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Asset, AssetWithAssignment, Effect, Nullable } from "../../utils/Interfaces";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { Tooltip, Typography, Chip, Box, IconButton } from "@mui/material";
import { selectExistingAssets, selectExistingAssetsLoaded } from "./assetsSlice";
import DataTable from "../../components/table/DataTable";
import Button from "@mui/material/Button";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { useWindowDimensions } from "../../utils/Hooks";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { Delete, Edit } from "@mui/icons-material";
import ModifyAssetModal from "./ModifyAssetModal";
import { conditionColor, conditionToName, lookupDisposalReason, DisposalReason } from "../../utils/Constants";
import { deleteAsset, updateAssets } from "./assetsFunctions";
import CreateAssetModal from "./CreateAssetModal";
import ConfirmDeleteModal from "../../components/confirmation/ConfirmDeleteModal";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

const AssetsManager = () => {
    const { height } = useWindowDimensions();

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const existingAssets: AssetWithAssignment[] = useAppSelector(selectExistingAssets);
    const existingAssetsLoaded: boolean = useAppSelector(selectExistingAssetsLoaded);

    const [selectedAsset, setSelectedAsset] = useState<Nullable<Asset>>(null);

    const [createAssetModalOpen, setCreateAssetModalOpen] = useState<boolean>(false);
    const [updateAssetModalOpen, setUpdateAssetModalOpen] = useState<boolean>(false);
    const [deleteAssetModalOpen, setDeleteAssetModalOpen] = useState<boolean>(false);

    const editCol = {
        field: "edit",
        headerName: "Edit",
        filterable: false,
        sortable: false,
        width: 60,
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Tooltip title={`Edit ${params.row.name}`}>
                    <IconButton
                        onClick={() => {
                            setSelectedAsset(params.row);
                            setUpdateAssetModalOpen(true);
                        }}
                    >
                        <Edit />
                    </IconButton>
                </Tooltip>
            );
        },
    };

    const deleteCol = {
        field: "delete",
        headerName: "Delete",
        filterable: false,
        sortable: false,
        width: 60,
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Tooltip title={`Delete ${params.row.name}`}>
                    <IconButton
                        onClick={() => {
                            setSelectedAsset(params.row);
                            setDeleteAssetModalOpen(true);
                        }}
                    >
                        <Delete color="error" />
                    </IconButton>
                </Tooltip>
            );
        },
    };

    const nameCol = {
        field: "name",
        headerName: "Asset Name",
        filterable: true,
        sortable: true,
        width: 200,
    };

    const manufacturerCol = {
        field: "manufacturer",
        headerName: "Manufacturer",
        filterable: true,
        sortable: true,
        width: 200,
    };

    const priceCol = {
        field: "price",
        headerName: "Unit Price",
        filterable: true,
        sortable: true,
        width: 200,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.price;
        },
        renderCell: (params: GridRenderCellParams) => {
            return <Typography>$ {params.row.price.toFixed(2)}</Typography>;
        },
    };

    const partNumberCol = {
        field: "part_number",
        headerName: "Part #",
        filterable: true,
        sortable: true,
        width: 200,
    };

    const serialNumberCol = {
        field: "serial_number",
        headerName: "Serial #",
        filterable: true,
        sortable: true,
        width: 250,
    };

    const conditionCol = {
        field: "condition",
        headerName: "Condition",
        filterable: true,
        sortable: true,
        width: 100,
        valueGetter: (params: GridRenderCellParams) => {
            return conditionToName(params.row.condition);
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Chip
                    // @ts-ignore
                    color={conditionColor(params.row.condition).replaceAll(".main", "")}
                    label={conditionToName(params.row.condition)}
                />
            );
        },
    };

    const disposalStatusCol = {
        field: "disposal_reason",
        headerName: "Disposal Status",
        filterable: true,
        sortable: true,
        width: 120,
        valueGetter: (params: GridRenderCellParams) => {
            return lookupDisposalReason(params.row.disposal_reason).name;
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Chip
                    // @ts-ignore
                    color={params.row.disposal_reason === DisposalReason.IN_SERVICE ? "success" : "error"}
                    label={lookupDisposalReason(params.row.disposal_reason).name}
                />
            );
        },
    };

    const assigneeCol = {
        field: "assignee",
        headerName: "Assigned To",
        filterable: true,
        sortable: true,
        width: 200,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.assigned_to === null
                ? "Unassigned"
                : `${params.row.assigned_to.first_name} ${params.row.assigned_to.last_name}`;
        },
        renderCell: (params: GridRenderCellParams) => {
            let assignee: string =
                params.row.assigned_to === null
                    ? "Unassigned"
                    : `${params.row.assigned_to.first_name} ${params.row.assigned_to.last_name}`;

            return (
                <Chip
                    // @ts-ignore
                    color={assignee === "Unassigned" ? "secondary" : "success"}
                    label={assignee}
                />
            );
        },
    };

    const assetColumns = [
        editCol,
        deleteCol,
        nameCol,
        manufacturerCol,
        partNumberCol,
        serialNumberCol,
        priceCol,
        conditionCol,
        disposalStatusCol,
        assigneeCol,
    ];

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Admin", route: "/?section=admin" },
                    { text: "Manage Assets", route: "/assets/manage" },
                ]}
            />

            <CreateAssetModal
                onAssetCreated={() => updateAssets(dispatch, () => {})}
                open={createAssetModalOpen}
                handleClose={() => setCreateAssetModalOpen(false)}
            />

            <ModifyAssetModal
                asset={selectedAsset}
                onAssetModified={() => updateAssets(dispatch, () => {})}
                open={updateAssetModalOpen}
                handleClose={() => setUpdateAssetModalOpen(false)}
            />

            <ConfirmDeleteModal
                confirmText={`delete ${selectedAsset ? selectedAsset.name : "--"}`}
                description="You are about to remove the following asset from the system permanently. Disposed assets should not be removed, this is for administrative cleanup only."
                onDelete={(modalCallback: Effect) => {
                    deleteAsset(
                        selectedAsset ? selectedAsset.id : 0,
                        dispatch,
                        () => {
                            modalCallback();
                            updateAssets(dispatch, () => {});
                            setSelectedAsset(null);
                        },
                        () => {}
                    );
                }}
                open={deleteAssetModalOpen}
                handleClose={() => setDeleteAssetModalOpen(false)}
            />

            {existingAssetsLoaded && existingAssets.length > 0 && (
                <DataTable tableName="ASSETS_LIST" height={height - 170} columns={assetColumns} data={existingAssets}>
                    <Button onClick={() => setCreateAssetModalOpen(true)}>Add Asset</Button>
                </DataTable>
            )}

            {existingAssetsLoaded && existingAssets.length === 0 && (
                <>
                    <Typography>Please create an asset to continue.</Typography>
                    <Button variant="contained" onClick={() => setCreateAssetModalOpen(true)}>
                        Create Asset
                    </Button>
                </>
            )}
        </Box>
    );
};

export default AssetsManager;
